import React from 'react';
import { Modal, Button } from '../common';

/**
 * Modal to view full submission details before adding content to passage
 */
export const SubmissionDetailModal = ({
  isOpen,
  onClose,
  submission,
  onAddContent,
  addedContent = []
}) => {
  if (!submission) return null;

  const { user, prose, images, audio, referencedAspects } = submission;

  // Check what's already added
  const isProseAdded = addedContent.some(
    c => c.submissionId === submission.id && c.contentType === 'prose'
  );

  const addedImageKeys = addedContent
    .filter(c => c.submissionId === submission.id && c.contentType === 'image')
    .map(c => c.mediaKey || c.mediaUrl);

  const addedAudioKeys = addedContent
    .filter(c => c.submissionId === submission.id && c.contentType === 'audio')
    .map(c => c.mediaKey || c.mediaUrl);

  const handleAddProse = () => {
    onAddContent({
      type: 'player_content',
      submissionId: submission.id,
      userId: user.id,
      content: prose.content,
      contentType: 'prose',
      referencedAspects: submission.referencedAspects
    });
  };

  const handleAddImage = (img) => {
    onAddContent({
      type: 'player_content',
      submissionId: submission.id,
      userId: user.id,
      content: img.originalFilename,
      contentType: 'image',
      mediaUrl: img.url,
      mediaKey: img.storageKey,
      referencedAspects: submission.referencedAspects
    });
  };

  const handleAddAudio = (aud) => {
    onAddContent({
      type: 'player_content',
      submissionId: submission.id,
      userId: user.id,
      content: aud.originalFilename,
      contentType: 'audio',
      mediaUrl: aud.url,
      mediaKey: aud.storageKey,
      referencedAspects: submission.referencedAspects
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${user.displayName}'s Submission`}
      size="lg"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Referenced Aspects */}
        {referencedAspects?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Referenced Aspects</h4>
            <div className="flex flex-wrap gap-1">
              {referencedAspects.map((aspect, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-1 rounded-full bg-primary-100 text-primary-700"
                >
                  {aspect.aspectName}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Prose */}
        {prose?.content && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Text Content</h4>
              <span className="text-xs text-gray-500">{prose.wordCount} words</span>
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
              {prose.content}
            </div>
            <div className="mt-3 pt-3 border-t">
              <Button
                size="sm"
                disabled={isProseAdded}
                onClick={handleAddProse}
              >
                {isProseAdded ? 'Already Added' : '+ Add Text to Passage'}
              </Button>
            </div>
          </div>
        )}

        {/* Images */}
        {images?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Images ({images.length})
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {images.map((img, idx) => {
                const isAdded = addedImageKeys.includes(img.storageKey) || addedImageKeys.includes(img.url);
                return (
                  <div key={idx} className="border rounded-lg overflow-hidden bg-gray-50">
                    <img
                      src={img.url}
                      alt={img.originalFilename}
                      className={`w-full h-32 object-cover ${isAdded ? 'opacity-50' : ''}`}
                    />
                    <div className="p-2">
                      <p className="text-xs text-gray-600 truncate mb-2">{img.originalFilename}</p>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full"
                        disabled={isAdded}
                        onClick={() => handleAddImage(img)}
                      >
                        {isAdded ? 'Added' : '+ Add'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Audio */}
        {audio?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Audio ({audio.length})
            </h4>
            <div className="space-y-2">
              {audio.map((aud, idx) => {
                const isAdded = addedAudioKeys.includes(aud.storageKey) || addedAudioKeys.includes(aud.url);
                return (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 truncate">{aud.originalFilename}</p>
                      <p className="text-xs text-gray-500">{Math.round(aud.durationSeconds)}s</p>
                      <audio controls className="mt-2 w-full h-8">
                        <source src={aud.url} />
                      </audio>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={isAdded}
                      onClick={() => handleAddAudio(aud)}
                    >
                      {isAdded ? 'Added' : '+ Add'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end mt-4 pt-4 border-t">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};
